import {
  aboutHeaderConnectionTypeDetailIcon,
  aboutHeaderConnectionTypeDetailItem,
  aboutHeaderConnectionTypeDetailText,
  aboutHeaderDescriptionAndTags,
  aboutHeaderDescriptionAndTagsContent,
  aboutHeaderDetailItem,
  aboutHeaderDetailLabel,
  aboutHeaderDetailsTextElide,
  aboutHeaderInfoIcon,
  aboutHeaderPlaceholderText,
  FeatureFlagsContext,
  filterOutNullAndUndefined,
  FormattedMarkupWrapperInPage,
  HorizontalFlexContainer,
  linkClass,
  messageForConnectionType,
  VerticalFlexContainer
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { classes } from 'typestyle';


import { Messages } from '../../gen-src/Localize';
import { SvgInfo } from '../../gen-src/icons/Info';
import { TableQuery_tables_DatabaseTable_tags } from '../../gen-src/queries/TableQuery';

export function renderFileType(connectionType: string) {
  const helpText = getHelpTextForFile(connectionType);
  const friendlyConnectionTypeName = messageForConnectionType(connectionType);
  return renderAboutFileTextDetailItem(Messages.FileType(), friendlyConnectionTypeName, helpText);
}

// This is based off of the ConnectToAllowedClasses, will need to manually add new types from that which need this messaging
function getHelpTextForFile(connectionType: string) {
  switch (connectionType) {
    case 'csv':
    case 'cloudfile:box-semistructpassivestore-direct':
    case 'cloudfile:box-textscan':
    case 'cloudfile:dropbox-semistructpassivestore-direct':
    case 'cloudfile:dropbox-textscan':
    case 'cloudfile:googledrive-semistructpassivestore-direct':
    case 'cloudfile:googledrive-textscan':
    case 'cloudfile:onedrive-semistructpassivestore-direct':
    case 'cloudfile:onedrive-textscan':
    case 'dataengine':
    case 'textclean':
    case 'textscan':
    case 'textscan-reader':
      return Messages.FileSingleTable();
    case 'cloudfile:box-excel-direct':
    case 'cloudfile:dropbox-excel-direct':
    case 'cloudfile:googledrive-excel-direct':
    case 'cloudfile:onedrive-excel-direct':
    case 'excel':
    case 'excel-direct':
    case 'excel-reader':
    case 'google-sheets':
    case 'hyper':
    case 'msaccess':
      return Messages.FileMultipleTables();
    default:
      return '';
  }
}

export function renderAboutHeaderTextDetailItem(label: string, value: string, testId: string): JSX.Element {
  return (
    <div className={aboutHeaderDetailItem} key={testId}>
      <VerticalFlexContainer fillFlexParent={true}>
        <div className={aboutHeaderDetailLabel}>{label}</div>
        <span data-tb-test-id={testId} title={value} className={aboutHeaderDetailsTextElide}>
          {value}
        </span>
      </VerticalFlexContainer>
    </div>
  );
}

export function renderEmbeddedAssetDescription(testId: string) {
  return renderTextDetailItem(
    Messages.DatabaseCategory(), Messages.EmbeddedAsset(), Messages.EmbeddedAssetHelpText(), testId
  );
}

function renderAboutFileTextDetailItem(label: string, value: string, helpText: string): JSX.Element {
  return renderTextDetailItem(label, value, helpText, 'table-details-connection-type-test-id');
}

function renderTextDetailItem(label: string, value: string, helpText: string, testId: string): JSX.Element {
  return (
    <div className={classes(aboutHeaderDetailItem, aboutHeaderConnectionTypeDetailItem)} key={testId}>
      <VerticalFlexContainer fillFlexParent={true}>
        <div className={aboutHeaderDetailLabel}>{label}</div>
        <HorizontalFlexContainer fillFlexParent={true}>
          <div data-tb-test-id={testId} title={value} className={aboutHeaderConnectionTypeDetailText}>
            {value}
          </div>
          <div title={helpText} className={aboutHeaderConnectionTypeDetailIcon}>
            <SvgInfo className={aboutHeaderInfoIcon} />
          </div>
        </HorizontalFlexContainer>
      </VerticalFlexContainer>
    </div>
  );
}

export function AboutHeaderDescriptionDetailItem(props: { description: string, testId: string }): JSX.Element {
  const { markupEditingEnabled } = React.useContext(FeatureFlagsContext);
  const { description, testId } = props;

  const markup = (markupEditingEnabled ? <FormattedMarkupWrapperInPage markup={description} /> : description);

  const fallback = (
    <div className={aboutHeaderPlaceholderText}>
      {Messages.NoDescriptionAvailable()}
    </div>
  );

  return (
    <div className={aboutHeaderDescriptionAndTags}>
      <div className={aboutHeaderDetailLabel}>{Messages.Description()}</div>
      <div className={aboutHeaderDescriptionAndTagsContent} data-tb-test-id={testId}>
        {
          description ? markup : fallback
        }
      </div>
    </div>
  );
}

export function renderAboutHeaderTagsDetailList(tags: ReadonlyArray<TableQuery_tables_DatabaseTable_tags>,
  testId: string,
  urls: SiteUrls,
  contentType: string) {
  // filters out null names in tags list
  const filtered = filterOutNullAndUndefined(tags.map(t => t.name));
  return (
    <div className={aboutHeaderDescriptionAndTags}>
      <div className={aboutHeaderDetailLabel}>{Messages.Tags()}</div>
      <div className={aboutHeaderDescriptionAndTagsContent} data-tb-test-id={testId}>
        {tags.length === 0 ?
          <div className={aboutHeaderPlaceholderText}>
            {Messages.NoTagsAvailable()}
          </div> :
          filtered.map((name, index) => (
            <React.Fragment key={name}>
              <Link
                to={contentType === 'DatabaseTable' ? urls.catalogTablesWithTagPath(name) :
                  urls.catalogDatabasesWithTagPath(name)}
                title={name}
                className={linkClass}
              >
                {name}
              </Link>
              {(tags.length - index) > 1 ? Messages.Comma() : null}
            </React.Fragment>
          ))
        }
      </div>
    </div>
  );
}

export function renderAboutHeaderDatabaseNameLinkItem(
  label: string,
  databaseName: string,
  databaseId: string,
  testId: string,
  urls: SiteUrls) {
  return (
    <div className={aboutHeaderDetailItem} key={testId}>
      <VerticalFlexContainer fillFlexParent={true}>
        <div className={aboutHeaderDetailLabel}>{label}</div>
        <Link
          to={urls.catalogDatabasePath(databaseId)}
          title={databaseName}
          className={aboutHeaderDetailsTextElide}
          data-tb-test-id={testId}
        >
          {databaseName}
        </Link>
      </VerticalFlexContainer>
    </div>
  );
}
